'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { resumeService } from '@/services/resumeService';

interface HeroSectionProps {
  onContactClick: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onContactClick }) => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentValueProp, setCurrentValueProp] = useState(0);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [loadingResume, setLoadingResume] = useState(false);

  const valuePropositions = [
  "Laboratory and operations leader with 10+ years of progressive experience across environmental, oil & gas, and corporate laboratory environments. Proven ability to lead teams of 5–12, manage P&L budgets from $200K–$1.5M, and deliver measurable improvements in turnaround time, cost control, and audit outcomes. Expert in QA/QC systems, regulatory compliance (ASTM, EPA, DOT), and operational optimization across both field-based and corporate lab settings. Recognized for building high-performing teams, maintaining audit-ready operations, and aligning laboratory performance with business objectives."];


  useEffect(() => {
    setIsHydrated(true);
    loadActiveResume();
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentValueProp((prev) => (prev + 1) % valuePropositions.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isHydrated, valuePropositions.length]);

  const loadActiveResume = async () => {
    try {
      const activeResume = await resumeService.getActiveResume();
      if (activeResume) {
        const latestVersion = await resumeService.getLatestVersion(activeResume.id);
        if (latestVersion?.fileUrl) {
          setResumeUrl(latestVersion.fileUrl);
        }
      }
    } catch (error) {
      console.error('Error loading resume:', error);
    }
  };

  const handleDownloadResume = async () => {
    if (!resumeUrl) {
      alert('Resume not available at this time.');
      return;
    }

    try {
      setLoadingResume(true);
      window.open(resumeUrl, '_blank');
    } catch (error) {
      console.error('Error downloading resume:', error);
      alert('Failed to download resume. Please try again.');
    } finally {
      setLoadingResume(false);
    }
  };

  const handleLoginClick = () => {
    router.push('/admin/login');
  };

  if (!isHydrated) {
    return (
      <section className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary min-h-[600px] lg:min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div className="text-white space-y-6">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                <Icon name="CheckBadgeIcon" size={20} variant="solid" className="text-accent" />
                <span className="text-sm font-medium">Available for Leadership Opportunities</span>
              </div>

              <div>
                <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4">
                  Curtis Kendal
                </h1>
                <p className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white/90 mb-2">
                  Laboratory & Operations Leader | QA/QC | Compliance | P&L Management
                </p>
                <p className="text-base sm:text-lg lg:text-xl font-medium text-white/80 mb-6">
                  Open to Remote & Hybrid Roles
                </p>
                <div className="h-20 sm:h-24">
                  <p className="text-lg sm:text-xl text-white/80 leading-relaxed">
                    {valuePropositions[0]}
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1a3780fad-1763300147666.png"
                  alt="Professional headshot of Kendal in business attire with confident smile in modern office setting"
                  className="w-full h-auto rounded-xl shadow-2xl" />
                
                <div className="mt-6 flex flex-col gap-3">
                  <button
                    onClick={onContactClick}
                    className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-white text-primary rounded-lg font-heading font-semibold text-sm hover:bg-white/90 transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5">
                    <Icon name="EnvelopeIcon" size={20} />
                    <span>Contact Me</span>
                  </button>
                  <a
                    href="https://www.linkedin.com/in/curtis-kendal"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-[#0077B5] text-white rounded-lg font-heading font-semibold text-sm hover:bg-[#005885] transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    <span>LinkedIn</span>
                  </a>
                  <button
                    onClick={handleDownloadResume}
                    disabled={!resumeUrl || loadingResume}
                    className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-accent text-accent-foreground rounded-lg font-heading font-semibold text-sm hover:bg-accent/90 transition-all duration-300 border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed">
                    <Icon name="DocumentArrowDownIcon" size={20} />
                    <span>{loadingResume ? 'Loading...' : 'Download Resume'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary min-h-[600px] lg:min-h-[700px] flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-transparent to-blue-500/10 animate-pulse"></div>
      
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
      
      <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div className="text-white space-y-6">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md px-5 py-2.5 rounded-full border-2 border-white/30 shadow-lg animate-pulse">
              <Icon name="CheckBadgeIcon" size={20} variant="solid" className="text-cyan-400" />
              <span className="text-sm font-semibold">Available for Leadership Opportunities</span>
            </div>

            <div>
              <h1 className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl leading-tight mb-4 drop-shadow-2xl">
                <span className="bg-gradient-to-r from-white via-cyan-100 to-white bg-clip-text text-transparent">
                  Curtis Kendal
                </span>
              </h1>
              <p className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white/95 mb-2 drop-shadow-lg">
                Laboratory & Operations Leader | QA/QC | Compliance | P&L Management
              </p>
              <p className="text-base sm:text-lg lg:text-xl font-medium text-white/90 mb-6 drop-shadow-md">
                Open to Remote & Hybrid Roles
              </p>
              <div className="h-20 sm:h-24">
                <p className="text-lg sm:text-xl text-white/90 leading-relaxed transition-opacity duration-500 drop-shadow-md">
                  {valuePropositions[currentValueProp]}
                </p>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-2xl blur-2xl"></div>
            
            <div className="relative bg-white/20 backdrop-blur-md rounded-2xl p-8 border-2 border-white/30 shadow-2xl transform hover:scale-105 transition-transform duration-300">
              <AppImage
                src="/assets/images/20190108_061456-1770566764055.jpg"
                alt="Professional headshot of Curtis Kendal in business attire with confident smile in modern office setting"
                className="w-full h-auto rounded-xl shadow-2xl" />
              
              <div className="mt-6 flex flex-col gap-3">
                <button
                  onClick={onContactClick}
                  className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-white text-primary rounded-lg font-heading font-semibold text-sm hover:bg-white/90 transition-all duration-300 hover:shadow-2xl hover:shadow-white/50 transform hover:-translate-y-1 hover:scale-105 border-2 border-white/30">
                  <Icon name="EnvelopeIcon" size={20} />
                  <span>Contact Me</span>
                </button>
                <a
                  href="https://www.linkedin.com/in/curtis-kendal"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-[#0077B5] text-white rounded-lg font-heading font-semibold text-sm hover:bg-[#005885] transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/50 transform hover:-translate-y-1 hover:scale-105">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  <span>LinkedIn</span>
                </a>
                <button
                  onClick={handleDownloadResume}
                  disabled={!resumeUrl || loadingResume}
                  className="w-full inline-flex items-center justify-center space-x-2 px-6 py-3 bg-accent text-accent-foreground rounded-lg font-heading font-semibold text-sm hover:bg-accent/90 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/50 transform hover:-translate-y-1 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none">
                  <Icon name="DocumentArrowDownIcon" size={20} />
                  <span>{loadingResume ? 'Loading...' : 'Download Resume'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;